import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../context/AuthContext";
import "./style.scss";

export default function Register() {
  const googleEnabled = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
  const navigate = useNavigate();
  const { register, loginWithGoogle, token } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      setError("Google sign up failed");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await loginWithGoogle(credential);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.message || "Google sign up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-page register-page w-full min-w-0">
      <div className="auth-card w-full max-w-md min-w-0 sm:max-w-lg">
        <Link to="/" className="back-to-home">
          ← Back to landing page
        </Link>
        <div className="auth-switch">
          <Link to="/login" className="auth-switch-btn">
            Login
          </Link>
          <Link to="/register" className="auth-switch-btn active">
            Sign Up
          </Link>
        </div>
        <h2 className="!text-2xl !leading-tight sm:!text-3xl">Create your account</h2>
        <p className="auth-subtitle !text-sm sm:!text-base">Start building your AI-powered health timeline.</p>
        <form onSubmit={onSubmit} className="auth-form">
          <label htmlFor="register-name">Full name</label>
          <input
            id="register-name"
            placeholder="John Doe"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <label htmlFor="register-email">Email</label>
          <input
            id="register-email"
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <label htmlFor="register-password">Password</label>
          <input
            id="register-password"
            placeholder="At least 6 characters"
            type="password"
            minLength={6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="mt-2 min-h-11 w-full rounded-[10px] px-4 py-3 text-base font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[8rem]"
          >
            {submitting ? "Creating account..." : "Sign Up"}
          </button>
        </form>
        {googleEnabled && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>
            <div className="google-auth">
              <GoogleLogin onSuccess={onGoogleSuccess} onError={() => setError("Google sign up failed")} />
            </div>
          </>
        )}
        {error && <p className="error">{error}</p>}
        <p className="auth-footer">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </section>
  );
}
